<script lang="ts">
	import Icon from "@components/common/Icon.svelte";
	import Media from "@components/common/Media.svelte";
	import copy from "@core/assets/icons/copy.svg?raw";
	import email from "@core/assets/icons/envelope.svg?raw";
	import info from "@core/assets/icons/info.svg?raw";
	import linkedin from "@core/assets/icons/linkedin-framed.svg?raw";
	import phone from "@core/assets/icons/phone.svg?raw";
	import type { Contact } from "@core/schema/Contact.js";
	import { getUsernameFromLinkedinUrl } from "@core/schema/utils/getUsernameFromLinkedinUrl.js";
	import { formatPhoneNumber } from "@core/utils/formatPhoneNumber.js";
	import imgChecker from "../assets/images/about-us/checker.jpg";
	import { type ContactActionType, contactActionTypes } from "./ContactActionType.js";
	import ContactCardActions from "./ContactCardActions.svelte";
	import Modal from "./common/Modal.svelte";

	const { member, isImportant }: { member: Contact; isImportant: boolean } = $props();
	const descriptionParagraphs = $derived((member.description ?? "").trim().split(/[\r\n]+/u));
	let isInfoModalOpen = $state(false);
	let openedType = $state<ContactActionType>();
	const value = $derived(
		openedType &&
			{
				linkedinUrl: member.linkedinUrl,
				email: member.email,
				phone: member.phone,
			}[openedType],
	);
	let copiedType = $state<ContactActionType>();
</script>

<div class={["overflow-hidden w-full relative", isImportant && "order-first col-span-full"]}>
	<div
		class={[
			"flex rounded-3xl bg-white/50 hover:bg-white transition-colors duration-300 backdrop-blur-[8px] p-4 w-full group h-[36rem]",
			isImportant
				? "flex-col xl:flex-row gap-x-4 md:min-h-[45rem] xl:min-h-[32rem]"
				: "flex-col max-w-[33rem] xl:min-h-[36rem]",
		]}
	>
		<div
			class={[
				"bg-white rounded-lg overflow-hidden ",
				isImportant ? "w-full xl:w-1/2" : "md:h-[14rem] xl:h-[14.25rem] max-w-full",
			]}
		>
			<Media
				source={member.photo ?? { src: imgChecker, type: "image/jpeg" }}
				alt={member.fullName}
				class={[
					"mx-auto",
					isImportant ? "h-full object-contain object-bottom" : "h-auto",
					member.photo ? "w-[70%]" : "w-full",
				]}
				width={isImportant ? 548 : 347}
				height={isImportant ? 548 : 347}
				sourcesets={{
					"<2xl": [isImportant ? 550 : 350, 0],
					">2xl": [isImportant ? 550 : 350, 0],
				}}
			/>
		</div>

		<div class="flex flex-col pt-6 flex-1">
			<div>
				<div
					class={[
						"group flex items-center justify-between mb-1",
						isImportant ? "items-center xl:mt-16" : "items-center",
					]}
				>
					<h3
						class={[
							"text-cmcsecondary uppercase font-[1000] font-druk-cond transition-transform duration-300",
							isImportant ? "text-6xl xl:text-9xl" : "text-4xl md:text-[2.5rem] lg:text-5xl",
						]}
					>
						{member.fullName}
					</h3>

					{#if descriptionParagraphs.length > 0}
						<button
							type="button"
							class="ml-4 lg:opacity-0 lg:translate-x-4 transition-all duration-300 md:group-hover:opacity-100 md:group-hover:translate-x-0"
							aria-label="Zobrazit více informací o {member.fullName}"
							onclick={(): void => {
								isInfoModalOpen = true;
							}}
						>
							<Icon
								icon={info}
								class={[
									"text-cmcsecondary border border-cmcsecondary p-[0.375rem] rounded-lg hover:bg-cmcsecondary hover:text-white transition-colors duration-300 size-8 md:size-10",
									isImportant && "md:size-14",
								]}
							/>
						</button>
					{/if}
				</div>

				<p class="text-xs/[1.6] md:text-[1rem]/[1.6] font-medium mb-8">
					{member.position}
				</p>
			</div>

			<div class="gap-2 flex flex-col items mt-auto">
				{#each contactActionTypes as type, index (index)}
					{@const value = member[type]}
					{#if value}
						<button
							type="button"
							class={[
								"flex items-center justify-between rounded-lg p-2 w-full text-left hover:brightness-95",
								"border-cmclightgray border transition-colors duration-700",
								copiedType === type ? "bg-cmcsuccess" : "bg-white",
							]}
							onclick={(): void => {
								openedType = type;
							}}
						>
							<div class="flex items-center gap-2 min-w-0">
								<Icon icon={{ linkedinUrl: linkedin, phone, email }[type]} class="size-10 text-cmcprimary" />
								<div class="truncate font-semibold text-sm xs:text-base max-w-[23ch] xs:max-w-full">
									{#if type === "linkedinUrl"}
										@{getUsernameFromLinkedinUrl(value)}
									{:else if type === "phone"}
										{formatPhoneNumber(value)}
									{:else}
										{value}
									{/if}
								</div>
							</div>

							<span
								class={[
									"ml-2 flex items-center gap-1 whitespace-nowrap transition-opacity duration-700",
									copiedType === type ? "opacity-75" : "opacity-0",
								]}
								aria-hidden={copiedType !== type}
							>
								<Icon icon={copy} class="size-6 inline-flex xs:hidden text-cmcprimary" />
								<span class="hidden xs:inline font-medium text-sm">zkopírováno</span>
							</span>
						</button>
					{/if}
				{/each}
			</div>
		</div>
	</div>

	{#each contactActionTypes as type (type)}
		<ContactCardActions
			isOpen={openedType === type}
			actionType={type}
			{value}
			direction="vertical"
			onclose={(reason): void => {
				if (reason === "copy") {
					copiedType = openedType;
					setTimeout(() => {
						if (reason === "copy") {
							copiedType = undefined;
						}
					}, 1000);
				}
				openedType = undefined;
			}}
		/>
	{/each}

	<!-- Modal -->
	{#if descriptionParagraphs.length > 0}
		<Modal bind:show={isInfoModalOpen}>
			<div class="flex flex-col max-w-[50rem] gap-4 max-h-full">
				<div class="bg-white rounded-lg overflow-hidden max-w-full min-h-[10rem] sm:h-[22rem] lg:h-[26rem]">
					<Media
						source={member.photo ?? { src: imgChecker, type: "image/jpeg" }}
						alt={member.fullName}
						wrapperClass="size-full"
						class="block size-full object-contain"
						width={800}
						height={416}
						sourcesets={{
							"<2xl": [420, 0],
							">2xl": [420, 0],
						}}
					/>
				</div>
				{#key isInfoModalOpen}
					<div class="mt-4 flex flex-col gap-6 mb-8 grow max-h-full overflow-hidden">
						<div class="px-8 text-cmcsecondary uppercase text-5xl font-druk-cond">
							{member.fullName}
						</div>
						<div class="px-8 flex flex-col gap-6 overflow-auto">
							{#each descriptionParagraphs as content (content)}
								<p class="leading-[1.4] font-medium">
									{content}
								</p>
							{/each}
						</div>
					</div>
				{/key}
			</div>
		</Modal>
	{/if}
</div>
